import { useState, useEffect } from 'react';
import axios from 'axios';
import { DataGrid } from '@mui/x-data-grid';
import { useSnackbar } from 'notistack';
import { Link, useNavigate } from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockResetIcon from '@mui/icons-material/LockReset';
import InventoryIcon from '@mui/icons-material/Inventory2';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const VendorTable = () => {
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [vendors, setVendors] = useState([]);

    const [dialog, setDialog] = useState(null); // { type: 'email' | 'reset', vendorId }
    const [passwordInput, setPasswordInput] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const fetchVendors = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/vendors');
            setVendors(data.vendors);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to load vendors", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVendors();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteHandler = async (id) => {
        if (!window.confirm("Delete this vendor? This can't be undone.")) return;
        try {
            await axios.delete(`/api/v1/admin/vendor/${id}`);
            enqueueSnackbar("Vendor removed", { variant: "success" });
            setVendors((prev) => prev.filter((v) => v._id !== id));
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to remove vendor", { variant: "error" });
        }
    };

    const openDialog = (type, vendorId) => {
        setDialog({ type, vendorId });
        setPasswordInput("");
    };

    const submitDialog = async () => {
        if (!passwordInput || passwordInput.length < 6) {
            enqueueSnackbar("Password must be at least 6 characters", { variant: "error" });
            return;
        }
        setSubmitting(true);
        try {
            if (dialog.type === 'email') {
                await axios.post(`/api/v1/admin/vendor/${dialog.vendorId}/send-email`, { password: passwordInput });
                enqueueSnackbar("Login email sent", { variant: "success" });
            } else {
                await axios.put(`/api/v1/admin/vendor/${dialog.vendorId}/reset-password`, { password: passwordInput });
                enqueueSnackbar("Password updated", { variant: "success" });
            }
            setDialog(null);
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Something went wrong", { variant: "error" });
        } finally {
            setSubmitting(false);
        }
    };

    const columns = [
        { field: "name", headerName: "Vendor", minWidth: 160, flex: 0.3 },
        { field: "brandName", headerName: "Brand", minWidth: 140, flex: 0.25 },
        { field: "email", headerName: "Email", minWidth: 200, flex: 0.35 },
        { field: "phone", headerName: "Phone", minWidth: 130, flex: 0.2 },
        {
            field: "active",
            headerName: "Status",
            minWidth: 100,
            flex: 0.2,
            renderCell: (params) => (
                params.row.active
                    ? <span className="text-green-700 bg-green-100 rounded-full px-2 py-0.5 text-xs font-medium">Active</span>
                    : <span className="text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 text-xs font-medium">Inactive</span>
            ),
        },
        {
            field: "actions",
            headerName: "Actions",
            minWidth: 220,
            flex: 0.4,
            sortable: false,
            renderCell: (params) => (
                <div className="flex items-center gap-0.5">
                    <Tooltip title="Manage Allocations & Sales">
                        <IconButton size="small" onClick={() => navigate(`/admin/vendor/${params.row.id}`)}>
                            <InventoryIcon sx={{ fontSize: "19px" }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Vendor">
                        <IconButton size="small" onClick={() => navigate(`/admin/vendor/${params.row.id}/edit`)}>
                            <EditIcon sx={{ fontSize: "19px" }} color="primary" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Send Login Email">
                        <IconButton size="small" onClick={() => openDialog('email', params.row.id)}>
                            <MailOutlineIcon sx={{ fontSize: "19px" }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Password">
                        <IconButton size="small" onClick={() => openDialog('reset', params.row.id)}>
                            <LockResetIcon sx={{ fontSize: "19px" }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Vendor">
                        <IconButton size="small" onClick={() => deleteHandler(params.row.id)}>
                            <DeleteIcon sx={{ fontSize: "19px" }} color="error" />
                        </IconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const rows = vendors.map((v) => ({ id: v._id, ...v }));

    return (
        <>
            <MetaData title="Admin: Vendors | 1313 Mart" />

            <div className="flex justify-between items-center mb-4">
                <h1 className="text-lg font-medium uppercase">Vendors</h1>
                <div className="flex gap-2">
                    <Link to="/admin/stores" className="py-2 px-4 rounded shadow font-medium text-primary-blue bg-white border border-primary-blue hover:shadow-lg">Manage Stores</Link>
                    <Link to="/admin/vendor/new" className="py-2 px-4 rounded shadow font-medium text-white bg-primary-blue hover:shadow-lg">New Vendor</Link>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg w-full" style={{ height: 600 }}>
                {loading ? <BackdropLoader /> : (
                    <DataGrid rows={rows} columns={columns} sx={{ border: 'none' }} />
                )}
            </div>

            <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
                <DialogTitle>{dialog?.type === 'email' ? 'Send Login Email' : 'Reset Password'}</DialogTitle>
                <DialogContent className="flex flex-col gap-3">
                    <p className="text-sm text-gray-600">
                        {dialog?.type === 'email'
                            ? "Enter the vendor's current password — it'll be included in the email along with their portal link."
                            : "Enter a new password for this vendor. They'll need to use this the next time they log in."}
                    </p>
                    <TextField
                        label={dialog?.type === 'email' ? "Current Password" : "New Password"}
                        type="text"
                        size="small"
                        value={passwordInput}
                        onChange={(e) => setPasswordInput(e.target.value)}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions>
                    <button onClick={() => setDialog(null)} className="py-2 px-4 text-sm text-gray-600">Cancel</button>
                    <button onClick={submitDialog} disabled={submitting} className="py-2 px-5 bg-primary-blue text-white text-sm font-medium rounded shadow hover:shadow-lg disabled:opacity-60">
                        {submitting ? "Working..." : dialog?.type === 'email' ? "Send Email" : "Reset Password"}
                    </button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default VendorTable;